import React, { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";
import MeaPage from "../../components/MeaPage";
import { useAuthContext } from "../../hooks/useAuthContext";
import accountsAPI from "../../services/accounts";
import trainingsAPI from "../../services/trainings";
import AgendaEventsComponent from "../trainings/events/components/AgendaEventsComponent";

export function TeacherAgendaPage() {
  const { school } = useLoaderData();
  const navigate = useNavigate();
  const { trainingId: routeTrainingId } = useParams();
  const { auth, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);
  const [preferenceError, setPreferenceError] = useState(false);
  const [savingPreference, setSavingPreference] = useState(false);

  const trainingsQuery = trainingsAPI.useGetList(school?.id, auth?.access_token, 1, 100, "");
  const trainings = trainingsQuery.data?.data || [];
  const preferredTrainingId = currentMembership?.preferences?.last_visited_training;
  const trainingId =
    (selectedTrainingId && trainings.some((training) => training.id === selectedTrainingId)
      ? selectedTrainingId
      : null) ||
    (trainings.some((training) => training.id === routeTrainingId)
      ? routeTrainingId
      : null) ||
    (trainings.some((training) => training.id === preferredTrainingId)
      ? preferredTrainingId
      : null) ||
    (trainings.length === 1 ? trainings[0].id : null);
  const training = trainings.find((item) => item.id === trainingId);

  const handleTrainingChange = async (newTrainingId) => {
    if (!newTrainingId || newTrainingId === trainingId) {
      return;
    }
    setSelectedTrainingId(newTrainingId);
    setPreferenceError(false);
    setSavingPreference(true);
    navigate(`/school/${school?.id}/teacher/agenda/${newTrainingId}`, { replace: true });
    try {
      const preferences = await accountsAPI.UpdateMemberPreferences(
        auth?.access_token,
        currentMembership?.id,
        newTrainingId
      );
      if (!preferences) {
        throw new Error("Réponse vide lors de l'enregistrement de la formation");
      }
      updateCurrentMembershipPreferences(preferences);
    } catch (error) {
      console.error("Impossible d'enregistrer la formation sélectionnée", error);
      setPreferenceError(true);
    } finally {
      setSavingPreference(false);
    }
  };

  const sectionOptions = [
    { label: "Mes séances de cours", value: "sessions" },
    { label: "Mes classes", value: "classes" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Agenda", value: "agenda" },
  ];

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value="agenda"
          options={sectionOptions}
          onChange={(event) => {
            if (event.value && event.value !== "agenda") {
              navigate(`/school/${school?.id}/teacher/${event.value}${trainingId ? `/${trainingId}` : ""}`);
            }
          }}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={trainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={(event) => handleTrainingChange(event.value)}
          placeholder="Sélectionner une formation"
          disabled={savingPreference}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
    </div>
  );

  return (
    <MeaPage
      title="Agenda"
      subtitle={training ? `Événements de la formation ${training.name}` : "Événements de la formation en cours"}
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${school?.id}/teacher`)}
      breadcrumb={customWorkspaceBar}
    >
      <div className="px-3 md:px-5">
        {trainingsQuery.isError && (
          <Message severity="error" text="Impossible de charger les formations." />
        )}
        {trainingsQuery.isLoading && <Skeleton width="100%" height="150px" />}
        {trainingsQuery.isSuccess && trainings.length === 0 && (
          <Message severity="info" text="Aucune formation disponible pour cette école." />
        )}
        {preferenceError && (
          <Message severity="warn" text="La formation sélectionnée n'a pas pu être mémorisée." />
        )}
        {trainingsQuery.isSuccess && trainings.length > 1 && !trainingId && (
          <Message severity="info" text="Sélectionnez une formation pour consulter son agenda." />
        )}
        <AgendaEventsComponent
          key={trainingId || "none"}
          schoolId={school?.id}
          trainingId={trainingId}
          accessToken={auth?.access_token}
        />
      </div>
    </MeaPage>
  );
}
